import { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faExpand, faVolumeHigh, faVolumeXmark, faXmark, faFire } from '@fortawesome/free-solid-svg-icons';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

interface VideoItem {
  id: number;
  title: string;
  description?: string;
  video_url: string | null;
  poster_url: string | null;
  status: 'active' | 'closed';
  viewer_count: number;
  created_at: string;
}

interface NewsItem {
  id: number;
  title: string;
  image_url?: string;
  category?: string;
  created_at: string;
  published_at: string;
  redirect_url?: string;
}

function getVideoSessionId(): string {
  let sid = sessionStorage.getItem('_video_sid');
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem('_video_sid', sid);
  }
  return sid;
}

function getVideoBrowserInfo(): Record<string, string> {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const tzCountryMap: Record<string, string> = {
      'Asia/Shanghai': 'CN', 'Asia/Chongqing': 'CN', 'Asia/Harbin': 'CN', 'Asia/Urumqi': 'CN',
      'Asia/Hong_Kong': 'HK', 'Asia/Macau': 'MO', 'Asia/Taipei': 'TW',
    };
    return {
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language || '',
      timezone: tz,
      country: tzCountryMap[tz] || '',
    };
  } catch {
    return {};
  }
}

function trackVideoEvent(videoId: number, eventType: string, eventData?: Record<string, any>) {
  api.post('/video-streams/track', {
    video_id: videoId,
    session_id: getVideoSessionId(),
    event_type: eventType,
    event_data: eventData ?? null,
    browser_info: getVideoBrowserInfo(),
  }).catch(() => {});
}

interface ZhongyaoDateConfig {
  label: string;
  price: number;
  image: string;
  date: string;
}

interface ZhongyaoPurchaseStatus {
  approved: number;
  pending: number;
}

interface ZhongyaoHistoryItem {
  id: number;
  date_key: string;
  date_label: string;
  amount: string;
  status: string;
  created_at: string;
}

const zhongyaoStatusLabels: Record<string, string> = {
  pending: '待支付',
  approved: '已预约',
  failed: '失败',
  expired: '已过期',
  cancelled: '已取消',
};

const zhongyaoStatusColors: Record<string, string> = {
  pending: 'text-yellow-600',
  approved: 'text-green-600',
  failed: 'text-red-500',
  expired: 'text-gray-400',
  cancelled: 'text-gray-400',
};

export default function Home() {
  const navigate = useNavigate();
  const isExternal = (u: string) => /^https?:\/\//.test(u);

  const [supportUrl, setSupportUrl] = useState<string>('/support');
  const [groupUrl, setGroupUrl] = useState<string>('/group');
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [news, setNews] = useState<NewsItem[]>([]);

  const [notices, setNotices] = useState<{ id: number; title: string; type: string; html?: string; image_url?: string; redirect_url?: string }[]>([]);
  const [showNotice, setShowNotice] = useState(false);
  const [noticeIndex, setNoticeIndex] = useState(0);

  const [homeVideos, setHomeVideos] = useState<VideoItem[]>([]);
  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [viewerCount, setViewerCount] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const viewerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startDurationTracking = useCallback((videoId: number) => {
    if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
    durationIntervalRef.current = setInterval(() => {
      if (videoRef.current && !videoRef.current.paused) {
        trackVideoEvent(videoId, 'duration', {
          current_time: Math.floor(videoRef.current.currentTime),
          total_duration: Math.floor(videoRef.current.duration || 0),
        });
      }
    }, 10000);
  }, []);

  const stopDurationTracking = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const openVideo = (video: VideoItem) => {
    if (video.status === 'closed') return;
    setActiveVideo(video);
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    trackVideoEvent(video.id, 'view');
    setViewerCount(video.viewer_count || 0);
    api.post(`/video-streams/${video.id}/view`)
      .then(({ data }) => setViewerCount(data.viewer_count))
      .catch(() => {});
    if (viewerIntervalRef.current) clearInterval(viewerIntervalRef.current);
    viewerIntervalRef.current = setInterval(() => {
      api.post(`/video-streams/${video.id}/view`)
        .then(({ data }) => setViewerCount(data.viewer_count))
        .catch(() => {});
    }, 15000);
  };

  const closeVideo = () => {
    stopDurationTracking();
    if (viewerIntervalRef.current) {
      clearInterval(viewerIntervalRef.current);
      viewerIntervalRef.current = null;
    }
    setActiveVideo(null);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (!videoRef.current || !activeVideo) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
      trackVideoEvent(activeVideo.id, 'play', { current_time: Math.floor(videoRef.current.currentTime) });
      startDurationTracking(activeVideo.id);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
      trackVideoEvent(activeVideo.id, 'pause', { current_time: Math.floor(videoRef.current.currentTime) });
      stopDurationTracking();
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const ct = videoRef.current.currentTime;
    const dur = videoRef.current.duration || 1;
    setProgress((ct / dur) * 100);
    setCurrentTime(ct);
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const handleEnded = () => {
    if (activeVideo) trackVideoEvent(activeVideo.id, 'complete', { total_duration: Math.floor(duration) });
    stopDurationTracking();
    setIsPlaying(false);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pct * (videoRef.current.duration || 0);
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) videoRef.current.requestFullscreen();
      else if ((videoRef.current as any).webkitEnterFullscreen) (videoRef.current as any).webkitEnterFullscreen();
    }
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  useEffect(() => {
    return () => {
      stopDurationTracking();
      if (viewerIntervalRef.current) clearInterval(viewerIntervalRef.current);
    };
  }, [stopDurationTracking]);

  const [chatReply, setChatReply] = useState<string>('添加土豆号 yhzj0001 联系客服，为您提供一对一专属服务。');
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<{ from: 'agent' | 'user'; text: string }[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatStarted, setChatStarted] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const [showZhongyao, setShowZhongyao] = useState(false);
  const [zhongyaoDates, setZhongyaoDates] = useState<Record<string, ZhongyaoDateConfig> | null>(null);
  const [zhongyaoPurchased, setZhongyaoPurchased] = useState<Record<string, ZhongyaoPurchaseStatus>>({});
  const [zhongyaoLoading, setZhongyaoLoading] = useState(false);
  const [zhongyaoPayModal, setZhongyaoPayModal] = useState(false);
  const [zhongyaoSelectedDate, setZhongyaoSelectedDate] = useState<string | null>(null);
  const [zhongyaoSubmitting, setZhongyaoSubmitting] = useState(false);
  const [zhongyaoShowHistory, setZhongyaoShowHistory] = useState(false);
  const [zhongyaoHistory, setZhongyaoHistory] = useState<ZhongyaoHistoryItem[]>([]);
  const [zhongyaoHistoryLoading, setZhongyaoHistoryLoading] = useState(false);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    api.get('/notices', { params: { page: 'home' } }).then(({ data }) => {
      const arr = Array.isArray(data) ? data : [];
      setNotices(arr);
      if (arr.length) { setNoticeIndex(0); setShowNotice(true); }
    }).catch(() => {});
    api.get('/home-links').then(({ data }) => {
      if (data) {
        if (typeof data.support === 'string' && data.support.length) setSupportUrl(data.support);
        if (typeof data.group === 'string' && data.group.length) setGroupUrl(data.group);
        if (typeof data.chat_reply === 'string' && data.chat_reply.length) setChatReply(data.chat_reply);
      }
    }).catch(() => {});
    api.get('/home-video').then(({ data }) => {
      if (data?.video_url) setVideoUrl(data.video_url);
    }).catch(() => {});
    api.get('/news').then(({ data }) => {
      setNews(Array.isArray(data) ? data : (data?.data ?? []));
    }).catch(() => {});
    api.get('/video-streams').then(({ data }) => {
      setHomeVideos(Array.isArray(data) ? data : []);
    }).catch(() => {});
    api.get('/zhongyao/status').then(({ data }) => {
      setZhongyaoDates(data.dates);
      setZhongyaoPurchased(data.purchased || {});
    }).catch(() => {});
  }, []);

  function openZhongyao() {
    setShowZhongyao(true);
    if (zhongyaoDates) return;
    setZhongyaoLoading(true);
    api.get('/zhongyao/status').then(res => {
      setZhongyaoDates(res.data.dates);
      setZhongyaoPurchased(res.data.purchased || {});
    }).catch(() => toast.error('加载失败'))
      .finally(() => setZhongyaoLoading(false));
  }

  function handleZhongyaoOpenDate(key: string) {
    const status = zhongyaoPurchased[key];
    if (status?.approved) { toast('您已预约该日期，不可重复预约'); return; }
    if (status?.pending) { toast('您有待支付的订单，请稍候再试'); return; }
    setZhongyaoSelectedDate(key);
    setZhongyaoPayModal(true);
  }

  async function handleZhongyaoPurchase(product: 'WX' | 'ALI' | 'ALI2') {
    if (!zhongyaoSelectedDate || zhongyaoSubmitting) return;
    setZhongyaoSubmitting(true);
    try {
      const res = await api.post('/zhongyao/purchase', { date_key: zhongyaoSelectedDate, product });
      const payUrl = res.data?.data?.pay_url;
      if (payUrl) window.location.href = payUrl;
      else toast.error('支付链接获取失败');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '预约失败');
    } finally {
      setZhongyaoSubmitting(false);
      setZhongyaoPayModal(false);
    }
  }

  async function loadZhongyaoHistory() {
    setZhongyaoShowHistory(true);
    setZhongyaoHistoryLoading(true);
    try {
      const res = await api.get('/zhongyao/history');
      setZhongyaoHistory(res.data || []);
    } catch { toast.error('加载记录失败'); }
    finally { setZhongyaoHistoryLoading(false); }
  }

  const openChat = () => { setChatMessages([]); setChatInput(''); setChatStarted(false); setShowChat(true); };
  const startChat = () => { setChatStarted(true); setChatMessages([{ from: 'agent', text: chatReply }]); };
  const sendMessage = () => {
    const text = chatInput.trim();
    if (!text) return;
    setChatMessages(prev => [...prev, { from: 'user', text }]);
    setChatInput('');
    setTimeout(() => { setChatMessages(prev => [...prev, { from: 'agent', text: chatReply }]); }, 600);
  };

  const quickActions = [
    { icon: '/images/home_shiming.png', label: '实名认证', href: '/kyc' },
    { icon: '/images/home_kefu.png', label: '在线客服', href: '/' },
    { icon: '/images/home_guanfnag.png', label: '官方群聊', href: groupUrl },
    { icon: '/images/home_yaoqing.png', label: '邀请好友', href: '/invite' },
  ];

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[url(/images/home_beijing.png)] bg-no-repeat bg-[length:100%_100%] pb-[50px]">
      <AuthGuard />
 
      {/* Video area */}
      <div className="px-4 relative z-10 pt-[150px]">
        {videoUrl ? (
          <video
            src={videoUrl}
            controls
            className="w-full rounded-xl bg-black aspect-video"
            poster="/images/home_baidi.png"
          />
        ) : (
          <div className="w-full rounded-xl bg-black aspect-video flex items-center justify-center">
            <img src="/images/home_baidi.png" alt="" className="w-full h-full object-cover rounded-xl" />
          </div>
        )}
      </div>

      {/* Quick action icons */}
      <div className="px-4 my-2">
        <div className="grid grid-cols-4 gap-3">
          {quickActions.map(({ icon, label, href, onClick }) => {
            if (onClick) {
              return (
                <button key={label} onClick={onClick} className="flex flex-col items-center gap-1.5">
                  <img src={icon} alt={label} className="w-full" /> 
                </button>
              );
            }
            const content = (
              <div className="flex flex-col items-center gap-1.5">
                <img src={icon} alt={label} className="w-full" /> 
              </div>
            );
            return isExternal(href) ? (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer">{content}</a>
            ) : (
              <Link key={label} to={href}>{content}</Link>
            );
          })}
        </div>
      </div>

      {/* Invite banner */}
      <div className="px-4 mb-2">
        <Link to="/invite" className="block relative">
          <img src="/images/home_yaoqinghaoyou.png" alt="邀请好友 领取福利" className="w-full rounded-xl" />
          <div className="absolute right-4 bottom-4">
            <span className="text-[#f5d998] text-sm font-bold">立即邀请 &gt;</span>
          </div>
        </Link>
      </div>

      {/* Tuipei card banner */}
      <div className="px-4 mb-2">
        <Link to="/cards" className="block relative">
          <img src="/images/home_tuipei.png" alt="稳健宝退赔卡" className="w-full rounded-xl" />
          <div className="absolute right-4 bottom-4">
            <span className="text-[#f5d998] text-sm font-bold">立即查看 &gt;</span>
          </div>
        </Link>
      </div>
 
      {/* Video section */}
      {homeVideos.length > 0 && (
        <div className="mx-4 px-4 mb-2 bg-[#fff] rounded-xl py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <img src="/images/home_zuixn.png" alt="" className="h-5" />
              <span className="font-bold text-base">会议播报</span>
            </div>
            <img src="/images/home_henxgian.png" alt="" className="h-px flex-1 mx-3 opacity-30" />
          </div>
          <div className="space-y-2">
            {homeVideos.map(video => (
              <div
                key={video.id}
                className={`rounded-xl overflow-hidden flex gap-3 p-2 ${video.status === 'closed' ? 'opacity-60' : 'cursor-pointer active:bg-gray-50'}`}
                onClick={() => openVideo(video)}
              >
                <div className="w-24 h-18 relative flex-shrink-0">
                  {video.poster_url ? (
                    <img src={video.poster_url} alt="" className={`w-24 h-18 object-cover rounded-lg ${video.status === 'closed' ? 'grayscale' : ''}`} />
                  ) : (
                    <div className="w-24 h-18 bg-gradient-to-br from-red-100 to-red-50 rounded-lg flex items-center justify-center">
                      <FontAwesomeIcon icon={faPlay} className="text-[#c4161c] text-sm" />
                    </div>
                  )}
                  {video.status === 'active' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-8 h-8 rounded-full bg-black/40 flex items-center justify-center">
                        <FontAwesomeIcon icon={faPlay} className="text-white text-xs ml-0.5" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex-1 flex flex-col justify-start min-w-0">
                  <p className="text-sm font-medium line-clamp-2">{video.title}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-[#c4161c] bg-[#c4161c]/10 px-2 py-0.5 rounded">会议视频</span>
                    <span className="text-black/40 text-xs">{video.viewer_count} 次观看</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* News section */}
      {/* <div className="mx-4 px-4 mb-2 bg-[#fff] rounded-xl py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <img src="/images/home_zuixn.png" alt="" className="h-5" />
            <span className="font-bold text-base">最新资讯</span>
          </div>
          <img src="/images/home_henxgian.png" alt="" className="h-px flex-1 mx-3 opacity-30" />
        </div>
        <div className="space-y-1">
          {news.length > 0 ? news.map(item => (
            <div
              key={item.id}
              className="bg-white/10 rounded-xl overflow-hidden flex gap-3 p-2 cursor-pointer"
              onClick={() => {
                if (item.redirect_url) {
                  if (isExternal(item.redirect_url)) window.open(item.redirect_url, '_blank');
                  else navigate(item.redirect_url);
                }
              }}
            >
              {item.image_url && (
                <img src={item.image_url} alt="" className="w-24 h-18 object-cover rounded-lg flex-shrink-0" />
              )}
              <div className="flex-1 flex flex-col justify-start min-w-0">
                <p className="text-sm font-medium line-clamp-2">{item.title}</p>
                <div className="flex items-center justify-between mt-1">
                  {item.category && (
                    <span className="text-xs text-[#f5d998] bg-[#f5d998]/15 px-2 py-0.5 rounded">{item.category}</span>
                  )}
                  <span className="text-black/40 text-xs">{item.published_at}</span>
                </div>
              </div>
            </div>
          )) : (
            <div className="text-white/40 text-center text-sm py-6">暂无资讯</div>
          )}
        </div>
      </div> */}

      {/* Policy link */}
      <div className="px-4 mb-4">
        <img src="/images/home_zhengce.png" alt="政策解读" className="w-full rounded-xl" />
      </div>
  
      {/* Notice modal */}
      {showNotice && notices.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="max-w-md w-[90%] rounded-xl bg-white shadow max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b shrink-0">
              <div className="text-md">{notices[noticeIndex]?.title ?? ''}</div>
            </div>
            <div className="p-3 overflow-y-auto">
              {(() => {
                const n = notices[noticeIndex];
                return n?.type === 'image' && n?.image_url
                  ? <img src={n.image_url} alt="notice" className="w-full h-auto rounded" />
                  : <div dangerouslySetInnerHTML={{ __html: n?.html || '' }} />;
              })()}
            </div>
            {notices[noticeIndex]?.redirect_url && (
              <div className="p-3 border-t shrink-0">
                <button
                  className="w-full py-2 rounded-lg bg-[#c4161c] text-white text-sm font-medium"
                  onClick={() => {
                    const url = notices[noticeIndex].redirect_url!;
                    setShowNotice(false);
                    if (isExternal(url)) window.open(url, '_blank', 'noopener,noreferrer');
                    else navigate(url);
                  }}
                >
                  查看详情
                </button>
              </div>
            )}
            <div className="p-2 flex justify-center border-t mt-3 shrink-0">
              <button className="text-md text-gray-500" onClick={() => {
                const next = noticeIndex + 1;
                if (next < notices.length) setNoticeIndex(next);
                else setShowNotice(false);
              }}>
                {noticeIndex + 1 < notices.length ? '下一条' : '关闭'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Zhongyao Appointment Modal */}
      {showZhongyao && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="relative w-full max-w-[330px] h-[90vh] mx-4 flex flex-col">
            <div className="flex justify-end mb-2">
              <button onClick={() => setShowZhongyao(false)} className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18" /><path d="M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto rounded-2xl">
              {zhongyaoLoading ? (
                <div className="flex items-center justify-center h-64"><div className="text-white text-sm">加载中...</div></div>
              ) : (
                <div className="relative">
                  <img src="/images/zhongyao_beijing.png" alt="" className="w-full" />
                  <div className="absolute top-0 left-0 right-0 w-full" style={{ paddingTop: '230px' }}>
                    <div className="px-4 pb-2">
                      <div className="relative">
                        <img src="/images/zhongyao_efu.png" alt="" className="w-full" />
                        <div className="absolute top-0 left-0 right-0 w-full" style={{ paddingTop: '35px' }}>
                          <div className="space-y-2 px-4 pb-2">
                            {zhongyaoDates && Object.keys(zhongyaoDates).map(key => {
                              const dateConfig = zhongyaoDates[key];
                              const isApproved = zhongyaoPurchased[key]?.approved > 0;
                              const isDisabled = isApproved;
                              return (
                                <div key={key} className="relative">
                                  <img src={dateConfig.image} alt={dateConfig.label} className="w-full" style={isDisabled ? { filter: 'grayscale(0.3)', opacity: 0.7 } : undefined} />
                                  <button onClick={() => handleZhongyaoOpenDate(key)} disabled={isDisabled}
                                    className="absolute" style={{ top: '15%', right: '4%', width: '28%', height: '70%', background: 'transparent', border: 'none', cursor: isDisabled ? 'not-allowed' : 'pointer' }}>
                                    {isApproved && <span className="text-white text-xs font-bold bg-green-600 px-2 py-1 rounded-full">已预约</span>}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                          <div className="px-4 pb-2"><img src="/images/zhongyao_chenggong.png" alt="" className="w-full" /></div>
                          <div className="px-4">
                            <button onClick={loadZhongyaoHistory} className="w-full"><img src="/images/zhongyao_chakan.png" alt="查看我的预约清单" className="w-full" /></button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="px-4 pb-8"><img src="/images/zhongyao_wenxin.png" alt="" className="w-full" /></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Zhongyao Payment Modal */}
      {zhongyaoPayModal && zhongyaoSelectedDate && zhongyaoDates && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={() => !zhongyaoSubmitting && setZhongyaoPayModal(false)}>
          <div className="bg-white rounded-2xl w-[85%] max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-center text-lg font-bold text-[#333] mb-1">大额提现预约 - {zhongyaoDates[zhongyaoSelectedDate].label}</h3>
            <p className="text-center text-2xl font-bold text-[#c41a1a] mb-4">¥{zhongyaoDates[zhongyaoSelectedDate].price}</p>
            <div className="space-y-2.5">
              <button onClick={() => handleZhongyaoPurchase('WX')} disabled={zhongyaoSubmitting} className="w-full py-3 rounded-xl text-base font-bold text-white disabled:opacity-60" style={{ background: 'linear-gradient(135deg, #07c160, #06ad56)' }}>
                {zhongyaoSubmitting ? '处理中...' : '微信支付'}
              </button>
              <button onClick={() => handleZhongyaoPurchase('ALI')} disabled={zhongyaoSubmitting} className="w-full py-3 rounded-xl text-base font-bold text-white disabled:opacity-60" style={{ background: 'linear-gradient(135deg, #1677ff, #0958d9)' }}>
                {zhongyaoSubmitting ? '处理中...' : '支付宝支付'}
              </button>
              <button onClick={() => handleZhongyaoPurchase('ALI2')} disabled={zhongyaoSubmitting} className="w-full py-3 rounded-xl text-base font-bold text-white disabled:opacity-60" style={{ background: 'linear-gradient(135deg, #1677ff, #0958d9)' }}>
                {zhongyaoSubmitting ? '处理中...' : '支付宝支付2'}
              </button>
            </div>
            <button onClick={() => setZhongyaoPayModal(false)} disabled={zhongyaoSubmitting} className="w-full mt-3 py-2 text-sm text-gray-400">取消</button>
          </div>
        </div>
      )}

      {/* Zhongyao History Modal */}
      {zhongyaoShowHistory && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={() => setZhongyaoShowHistory(false)}>
          <div className="bg-white rounded-2xl w-[90%] max-w-sm max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100"><h3 className="text-center text-lg font-bold text-[#333]">我的预约清单</h3></div>
            <div className="flex-1 overflow-y-auto p-4">
              {zhongyaoHistoryLoading ? (
                <div className="text-center text-gray-400 text-sm py-8">加载中...</div>
              ) : zhongyaoHistory.length === 0 ? (
                <div className="text-center text-gray-400 text-sm py-8">暂无记录</div>
              ) : (
                <div className="space-y-3">
                  {zhongyaoHistory.map(item => (
                    <div key={item.id} className="bg-gray-50 rounded-xl px-4 py-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-bold text-[#333]">{item.date_label}</span>
                        <span className={`text-xs font-bold ${zhongyaoStatusColors[item.status] || 'text-gray-400'}`}>{zhongyaoStatusLabels[item.status] || item.status}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{item.created_at}</span>
                        <span className="text-sm font-bold text-[#c41a1a]">¥{item.amount}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-100">
              <button onClick={() => setZhongyaoShowHistory(false)} className="w-full py-2.5 rounded-xl text-sm text-gray-500 bg-gray-100">关闭</button>
            </div>
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {activeVideo && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4">
            <div className="flex items-center justify-between max-w-md mx-auto">
              <button onClick={closeVideo} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 backdrop-blur-md text-white active:bg-white/20 transition">
                <FontAwesomeIcon icon={faXmark} className="text-lg" />
              </button>
              <h2 className="flex-1 text-center text-white font-semibold text-sm truncate px-3">{activeVideo.title}</h2>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-600/90 backdrop-blur-sm rounded-full shadow-lg shadow-red-500/30">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  <span className="text-white text-xs font-bold tracking-wide">回放</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-black/40 backdrop-blur-sm rounded-full">
                  <FontAwesomeIcon icon={faFire} className="text-orange-400 text-xs" />
                  <span className="text-white text-xs font-medium">{viewerCount.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center" onClick={togglePlay}>
            <video
              ref={videoRef}
              src={activeVideo.video_url || ''}
              poster={activeVideo.poster_url || undefined}
              className="w-full h-full object-contain"
              playsInline
              muted={isMuted}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleEnded}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/10 animate-pulse">
                  <FontAwesomeIcon icon={faPlay} className="text-white text-3xl ml-1.5" />
                </div>
              </div>
            )}
          </div>

          <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/80 to-transparent pb-safe">
            <div className="max-w-md mx-auto px-4 pb-4 pt-10">
              <div className="w-full h-1.5 bg-white/10 rounded-full cursor-pointer mb-3 group/bar" onClick={handleProgressClick}>
                <div className="h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-full relative transition-all" style={{ width: `${progress}%` }}>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-lg shadow-red-500/30 opacity-0 group-hover/bar:opacity-100 transition" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button onClick={togglePlay} className="text-white p-2">
                    <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} className="text-lg" />
                  </button>
                  <button onClick={toggleMute} className="text-white/70 p-2">
                    <FontAwesomeIcon icon={isMuted ? faVolumeXmark : faVolumeHigh} className="text-base" />
                  </button>
                  <span className="text-white/50 text-xs font-mono">{formatTime(currentTime)} / {formatTime(duration)}</span>
                </div>
                <button onClick={handleFullscreen} className="text-white/70 p-2">
                  <FontAwesomeIcon icon={faExpand} className="text-base" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat box */}
      {showChat && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden mx-4">
            <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-100">
              <button onClick={() => setShowChat(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
              <span className="text-sm font-medium text-gray-800">在线客服</span>
              <button onClick={() => setShowChat(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /></svg>
              </button>
            </div>
            {!chatStarted ? (
              <div className="flex-1 flex flex-col items-center justify-center px-6">
                <button onClick={startChat} className="w-full py-3 rounded-xl bg-[#c4161c] text-white font-semibold text-base shadow-lg">立即咨询</button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {msg.from === 'agent' && <div className="w-8 h-8 rounded-full bg-[#c4161c] flex items-center justify-center text-white text-xs font-bold mr-2 flex-shrink-0 mt-0.5">客</div>}
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${msg.from === 'user' ? 'bg-[#c4161c] text-white rounded-br-md' : 'bg-white text-gray-800 shadow-sm rounded-bl-md'}`}>{msg.text}</div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <div className="px-3 py-3 bg-white border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }} placeholder="输入消息..." className="flex-1 px-4 py-2.5 rounded-full bg-gray-100 text-sm outline-none focus:ring-2 focus:ring-[#c4161c]/30" />
                    <button onClick={sendMessage} disabled={!chatInput.trim()} className="w-10 h-10 rounded-full bg-[#c4161c] flex items-center justify-center text-white disabled:opacity-40 flex-shrink-0">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" /></svg>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
